import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEntrance } from '../hooks/useEntrance';
import CallReport from './CallReport';
import fetchWithRetry from '../utils/fetchWithRetry';

function safeString(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(safeString).join(', ');
  if (typeof value === 'object') {
    return Object.entries(value).map(([k, v]) => `${k}: ${v}`).join(' | ');
  }
  return String(value);
}

const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.port === '3000' ? `http://${window.location.hostname}:3001` : '');

const SCORE_FILTERS = [
  { label: 'Any Score', value: '' },
  { label: '7+ (Good)', value: '7' },
  { label: '5+ (OK)', value: '5' },
  { label: '< 5 (Low)', value: '0-4' },
];

const DATE_FILTERS = [
  { label: 'All Time', value: '' },
  { label: 'Today', value: 'today' },
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
];

function getDateRange(value) {
  if (!value) return {};
  const now = Date.now();
  const dayMs = 86400000;
  const todayStart = new Date().setHours(0, 0, 0, 0);
  if (value === 'today') return { dateFrom: todayStart };
  if (value === 'week') return { dateFrom: now - 7 * dayMs };
  if (value === 'month') return { dateFrom: now - 30 * dayMs };
  return {};
}

const ENTRANCE_OPTS = { threshold: 0.05 };

export default function CallHistory({ reports, latestReportId, onClearLatest, token }) {
  const listRef = useRef(null);
  useEntrance(listRef, ENTRANCE_OPTS);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const [selectedReport, setSelectedReport] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const limit = 20;

  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [scoreFilter, setScoreFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [starredFilter, setStarredFilter] = useState(false);

  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (searchDebounced) params.set('search', searchDebounced);
      if (starredFilter) params.set('starred', '1');
      if (scoreFilter) {
        if (scoreFilter === '0-4') {
          params.set('scoreMax', '4');
        } else {
          params.set('scoreMin', scoreFilter);
        }
      }
      const dr = getDateRange(dateFilter);
      if (dr.dateFrom) params.set('dateFrom', String(dr.dateFrom));
      if (dr.dateTo) params.set('dateTo', String(dr.dateTo));

      const res = await fetchWithRetry(`${API_BASE}/api/reports?${params}`, { headers: authHeaders });
      const data = await res.json();
      setSavedReports(data.reports || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, searchDebounced, scoreFilter, dateFilter, starredFilter, token]);

  useEffect(() => { fetchReports(); }, [fetchReports]);
  useEffect(() => { if (reports.length > 0) fetchReports(); }, [reports.length]);

  useEffect(() => {
    if (latestReportId) {
      const fromWs = reports.find((r) => r.callSid === latestReportId);
      if (fromWs) {
        setSelectedReport(fromWs);
        onClearLatest();
      }
    }
  }, [latestReportId, reports, onClearLatest]);

  const handleSelect = async (callSid) => {
    const fromWs = reports.find((r) => r.callSid === callSid);
    if (fromWs) { setSelectedReport(fromWs); return; }
    try {
      const res = await fetchWithRetry(`${API_BASE}/api/reports/${callSid}`, { headers: authHeaders });
      if (res.ok) setSelectedReport(await res.json());
    } catch { /* ignore */ }
  };

  const handleDownload = (report) => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-report-${report.callSid}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleToggleStar = async (callSid, currentStarred, e) => {
    e.stopPropagation();
    try {
      const res = await fetchWithRetry(`${API_BASE}/api/reports/${callSid}`, {
        method: 'PATCH',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ starred: !currentStarred }),
      });
      if (res.ok) {
        setSavedReports((prev) =>
          prev.map((r) => r.callSid === callSid ? { ...r, starred: !currentStarred } : r)
        );
      }
    } catch { /* ignore */ }
  };

  const handleDelete = async (callSid) => {
    try {
      await fetchWithRetry(`${API_BASE}/api/reports/${callSid}`, {
        method: 'DELETE', headers: authHeaders,
      });
      setSelectedReport(null);
      fetchReports();
    } catch { /* ignore */ }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} report(s)?`)) return;
    try {
      await fetchWithRetry(`${API_BASE}/api/reports/bulk-delete`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ callSids: [...selectedIds] }),
      });
      setSelectedIds(new Set());
      setBulkMode(false);
      fetchReports();
    } catch { /* ignore */ }
  };

  const handleExportSelected = () => {
    const toExport = savedReports.filter((r) => selectedIds.has(r.callSid));
    const blob = new Blob([JSON.stringify(toExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'call-reports-export.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === savedReports.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(savedReports.map((r) => r.callSid)));
    }
  };

  const toggleSelect = (callSid, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(callSid)) next.delete(callSid);
      else next.add(callSid);
      return next;
    });
  };

  const handleReportUpdate = (updatedReport) => {
    setSelectedReport(updatedReport);
    fetchReports();
  };

  if (selectedReport) {
    return (
      <CallReport
        report={selectedReport}
        onBack={() => setSelectedReport(null)}
        onDownload={() => handleDownload(selectedReport)}
        onDelete={() => handleDelete(selectedReport.callSid)}
        onUpdate={handleReportUpdate}
        token={token}
      />
    );
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="call-history" ref={listRef}>
      <div className="panel-header">Call History</div>

      {/* Toolbar: search + filters */}
      <div className="history-toolbar">
        <input
          type="text"
          className="history-search-input"
          placeholder="Search calls..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        />
        <select
          className="history-filter-select"
          value={scoreFilter}
          onChange={(e) => { setScoreFilter(e.target.value); setPage(0); }}
        >
          {SCORE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className="history-filter-select"
          value={dateFilter}
          onChange={(e) => { setDateFilter(e.target.value); setPage(0); }}
        >
          {DATE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
        <button
          className={`history-filter-btn ${starredFilter ? 'history-filter-active' : ''}`}
          onClick={() => { setStarredFilter(!starredFilter); setPage(0); }}
          title="Show starred only"
        >
          {starredFilter ? '★' : '☆'}
        </button>
        <button
          className={`history-filter-btn ${bulkMode ? 'history-filter-active' : ''}`}
          onClick={() => { setBulkMode(!bulkMode); setSelectedIds(new Set()); }}
        >
          Select
        </button>
      </div>

      {/* Bulk actions bar */}
      {bulkMode && (
        <div className="history-bulk-bar">
          <button className="history-bulk-btn" onClick={toggleSelectAll}>
            {selectedIds.size === savedReports.length ? '☐ Deselect All' : '☑ Select All'}
          </button>
          <span className="history-bulk-count">{selectedIds.size} selected</span>
          <button
            className="history-bulk-btn history-bulk-danger"
            onClick={handleBulkDelete}
            disabled={selectedIds.size === 0}
          >
            Delete
          </button>
          <button
            className="history-bulk-btn"
            onClick={handleExportSelected}
            disabled={selectedIds.size === 0}
          >
            Export
          </button>
          <button className="history-bulk-btn" onClick={() => { setBulkMode(false); setSelectedIds(new Set()); }}>
            Cancel
          </button>
        </div>
      )}

      {loading ? (
        <div className="history-empty">Loading...</div>
      ) : savedReports.length === 0 ? (
        <div className="history-empty">
          {search || scoreFilter || dateFilter || starredFilter
            ? 'No reports match your filters.'
            : 'No call reports yet. Reports are generated automatically when a call ends.'}
        </div>
      ) : (
        <div className="history-list">
          {savedReports.map((entry) => (
            <div
              key={entry.callSid}
              className={`history-card ${selectedIds.has(entry.callSid) ? 'history-card-selected' : ''}`}
              onClick={() => !bulkMode && handleSelect(entry.callSid)}
            >
              <div className="history-card-header">
                <div className="history-card-left">
                  {bulkMode && (
                    <input
                      type="checkbox"
                      className="history-checkbox"
                      checked={selectedIds.has(entry.callSid)}
                      onChange={(e) => toggleSelect(entry.callSid, e)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <button
                    className={`history-star ${entry.starred ? 'history-star-active' : ''}`}
                    onClick={(e) => handleToggleStar(entry.callSid, entry.starred, e)}
                    title={entry.starred ? 'Unstar' : 'Star'}
                  >
                    {entry.starred ? '★' : '☆'}
                  </button>
                  <span className="history-card-date">
                    {new Date(entry.startedAt).toLocaleString()}
                  </span>
                </div>
                {entry.stats && (
                  <span className="history-card-badge">
                    {entry.stats.suggestionCount} suggestions
                  </span>
                )}
              </div>
              <div className="history-card-row">
                <span className="history-card-duration">
                  {entry.duration ? Math.round(entry.duration / 1000) + 's' : '—'}
                </span>
                {entry.platformCost && (
                  <span className="history-card-fee">${entry.platformCost.dollars}</span>
                )}
                {entry.notes && <span className="history-notes-indicator" title="Has notes">📝</span>}
              </div>
              {(entry.summaryError || entry.summary?.error) && (
                <div className="history-card-error">Summary unavailable</div>
              )}
              {(entry.summaryVerdict || entry.summary?.verdict) && (
                <div className={`history-card-verdict ${(entry.summaryError || entry.summary?.error) ? 'verdict-error' : ''}`}>
                  {safeString(entry.summaryVerdict || entry.summary?.verdict)}
                </div>
              )}
              {(entry.summaryScore ?? entry.summary?.score) != null && (
                <div className="history-card-score">
                  Score: <strong>{entry.summaryScore ?? entry.summary.score}</strong>/10
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="history-pagination">
          <button
            className="history-page-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            ← Prev
          </button>
          <span className="history-page-info">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="history-page-btn"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
